import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { ITestimonial } from '../../common/model/testimonial.model';
import useI18n from '../../common/hooks/useI18n';

import { TestimonialsCarousel } from '.';

type Props = {
  testimonials: ITestimonial[];
  className?: string;
};

const Animation = ({ src }) => {
  useEffect(() => {
    import('@dotlottie/player-component');
  }, []);
  return (
    <dotlottie-player
      src={src}
      autoplay
      loop
      style={{ height: '100%', width: '100%' }}
    />
  );
};

const SectionTitle = styled.h3`
  max-width: ${(props) => props.theme.grid.getGridColumns(14, 1)};
  @media ${(props) => props.theme.breakpoints.sm} {
    max-width: ${(props) => props.theme.grid.getGridColumns(13, 1)};
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    max-width: inherit;
  }
  color: ${(props) => props.theme.colors.secondary};
  ${(props) => props.theme.font.h2}
  margin-left: 24px;
`;

const Divider = styled.hr`
  width: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-top: 16px;
  margin-bottom: 16px;
  margin-left: calc(24px + var(--gutter));
  margin-right: 0;
  border: 1px solid ${(props) => props.theme.colors.primary};
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};
`;

const AnimationContainer = styled.div`
  width: 40%;
  height: 100%;
  float: left;
  margin-top: -60px;

  @media ${(props) => props.theme.breakpoints.mdd} {
    float: initial;
    width: 100%;
  }
`;

const TestimonialsContainer = styled.div`
  position: relative;
  width: 60%;
  height: 100%;
  display: inline-block;
  z-index: 100;

  @media ${(props) => props.theme.breakpoints.mdd} {
    display: initial;
    width: 100%;
  }
`;

const Testimonials: React.FC<Props> = ({ testimonials, className }) => {
  const i18n = useI18n();

  const title = useMemo(() => i18n.t('testimonials.title'), [i18n]);

  return (
    <div className={className}>
      <TestimonialsContainer>
        <SectionTitle
          key={title.substring(0, 10) + new Date().getTime()}
          as="h3"
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
        <Divider />
        <TestimonialsCarousel testimonials={testimonials} />
      </TestimonialsContainer>
      <AnimationContainer>
        <Animation src="/images/testimonials.lottie" />
      </AnimationContainer>
    </div>
  );
};

export default styled(Testimonials)`
  margin-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  padding-top: 60px;
  padding-bottom: 60px;
`;
